// src/hooks/useAlertDialog.js

import { useState, useCallback } from 'react';

const useAlertDialog = () => {
  const [dialogState, setDialogState] = useState({
    show: false,
    title: '',
    message: '',
    variant: 'info',
    icon: null,
    autoClose: false,
    autoCloseDelay: 3000,
    showCloseButton: true,
  });

  const showAlert = useCallback(({
    title,
    message,
    variant = 'info',
    icon = null,
    autoClose = false,
    autoCloseDelay = 3000,
    showCloseButton = true,
  }) => {
    setDialogState({
      show: true,
      title,
      message,
      variant,
      icon,
      autoClose,
      autoCloseDelay,
      showCloseButton,
    });

    // If autoClose is enabled, return a promise that resolves when dialog closes
    if (autoClose) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, autoCloseDelay);
      });
    }
  }, []);

  const hideAlert = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      show: false,
    }));
  }, []);

  // Convenience methods for different alert types
  const showSuccess = useCallback((message, title, options = {}) => {
    return showAlert({
      title,
      message,
      variant: 'success',
      icon: options.icon,
      autoClose: options.autoClose,
      autoCloseDelay: options.autoCloseDelay,
      showCloseButton: options.showCloseButton,
    });
  }, [showAlert]);

  const showError = useCallback((message, title, options = {}) => {
    return showAlert({
      title,
      message,
      variant: 'danger',
      icon: options.icon,
      autoClose: options.autoClose,
      autoCloseDelay: options.autoCloseDelay,
      showCloseButton: options.showCloseButton,
    });
  }, [showAlert]);

  const showWarning = useCallback((message, title, options = {}) => {
    return showAlert({
      title,
      message,
      variant: 'warning',
      icon: options.icon,
      autoClose: options.autoClose,
      autoCloseDelay: options.autoCloseDelay,
      showCloseButton: options.showCloseButton,
    });
  }, [showAlert]);

  const showInfo = useCallback((message, title, options = {}) => {
    return showAlert({
      title,
      message,
      variant: 'info',
      icon: options.icon,
      autoClose: options.autoClose,
      autoCloseDelay: options.autoCloseDelay,
      showCloseButton: options.showCloseButton,
    });
  }, [showAlert]);

  return {
    dialogState,
    showAlert,
    hideAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default useAlertDialog;
